import { frontendURL } from '../../../helper/URLHelper';
import { isFeatureFlagEnabledForAccount } from '../../../helper/featureFlagHelper';
import store from '../../../store';

const ClaudiaLayout = () => import('./components/ClaudiaLayout.vue');
const EddieView = () => import('../eddie/pages/EddieView.vue');
const KnowledgeBaseMicrofrontend = () =>
  import(
    '../../../../microfrontends/knowledge-base/KnowledgeBaseMicrofrontend.vue'
  );
const ImprovementCenterMicrofrontend = () =>
  import(
    '../../../../microfrontends/claudia/ImprovementCenterMicrofrontend.vue'
  );
const ClaudiaContextEnrichmentMicrofrontend = () =>
  import(
    '../../../../microfrontends/claudia/ClaudiaContextEnrichmentMicrofrontend.vue'
  );

const IMPROVEMENT_CENTER_FLAG = 'enable_improvement_center';

// The global route guard (`routeHelpers.js`) only checks `meta.permissions`,
// never `meta.newFeatureFlag`. Without this the Improvement Center route would
// stay reachable by URL even with the flag off. Redirect to the ClaudIA root
// (Agents) when the flag is disabled so the route is gated, not just hidden.
//
// Resolve the flag from `currentUser.accounts` — the SAME source the sidebar
// gate (`isNewFeatureFlagEnabled` → `getCurrentAccount`) reads, so the menu
// item and the route never disagree. The `accounts/get` payload whitelists
// `custom_attributes` and drops `feature_flags`, so reading it there would
// always gate off. We scope by the *target* account (`to.params.accountId`)
// instead of the current-account getter, which during `beforeEnter` still
// points at the previous account (the navigation isn't committed to the
// vuex-router-synced route yet). `setUser` resolves before the first
// navigation (see `initalizeRouter`), so `currentUser.accounts` is populated.
const isImprovementCenterEnabled = accountId => {
  const accounts = store.getters.getCurrentUser?.accounts || [];
  const account = accounts.find(({ id }) => id === Number(accountId));
  // Delegate value extraction to the shared helper (dotted-path aware, same as
  // the `isNewFeatureFlagEnabled` getter). Account selection stays here: we
  // scope by the *target* account, not the current-account getter.
  return isFeatureFlagEnabledForAccount(account, IMPROVEMENT_CENTER_FLAG);
};

const guardImprovementCenterFlag = (to, from, next) => {
  const { accountId } = to.params;
  if (isImprovementCenterEnabled(accountId)) {
    next();
  } else {
    // Distinguish "flag genuinely off" from the anomalous "user is loaded but the target account
    // isn't in currentUser.accounts" branch. The latter shouldn't happen once `setUser` has resolved
    // (see comment above), so surface it for observability instead of silently redirecting.
    const accounts = store.getters.getCurrentUser?.accounts || [];
    const accountFound = accounts.some(({ id }) => id === Number(accountId));
    if (accounts.length && !accountFound) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ImprovementCenter] Target account ${accountId} not found in currentUser.accounts; treating flag as disabled.`
      );
    }
    next(frontendURL(`accounts/${accountId}/claudia/agents`));
  }
};

const ClaudiaControlledFlowsMicrofrontend = () =>
  import(
    '../../../../microfrontends/claudia/ClaudiaControlledFlowsMicrofrontend.vue'
  );
const ClaudiaAgentsView = () => import('./pages/ClaudiaAgentsView.vue');
const ClaudiaToolsView = () => import('./pages/ClaudiaToolsView.vue');
const ClaudiaMcpView = () => import('./pages/ClaudiaMcpView.vue');
const ClaudiaAiTaggingAndFieldFillingView = () =>
  import('./pages/ClaudiaAiTaggingAndFieldFillingView.vue');
const ClaudiaSettingsView = () => import('./pages/ClaudiaSettingsView.vue');
const ClaudiaDeployView = () => import('./pages/ClaudiaDeployView.vue');
const ClaudiaCreateProjectView = () =>
  import('./pages/ClaudiaCreateProjectView.vue');

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/claudia'),
      name: 'claudia_app',
      component: ClaudiaLayout,
      meta: {
        permissions: ['administrator', 'cx_engineer'],
      },
      children: [
        {
          path: '',
          name: 'claudia_default',
          // Match parent's permissions so the global beforeEach guard's
          // `routeIsAccessibleFor` check passes for admins/cx_engineers.
          // Without this meta the empty child has no permissions array and
          // the legacy `validateLoggedInRoutes` redirects to /dashboard
          // before our beforeEnter ever fires.
          meta: { permissions: ['administrator', 'cx_engineer'] },
          beforeEnter: (to, from, next) => {
            const { accountId } = to.params;
            next(frontendURL(`accounts/${accountId}/claudia/agents`));
          },
        },
        {
          path: 'view',
          name: 'eddie_view_legacy',
          // The legacy /claudia/view route used to mount the entire Eddie
          // dashboard. The CRUD/dashboard portion has moved into the native
          // claudia-app ControlledFlows module — redirect any existing
          // bookmarks back to the new entry point instead of rendering the
          // old iframe-only page.
          redirect: to => ({
            name: 'claudia_agents',
            params: { accountId: to.params.accountId },
          }),
          meta: { permissions: ['administrator', 'cx_engineer'] },
        },
        {
          path: 'eddie',
          name: 'eddie_view',
          component: EddieView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'controlled-flows',
          name: 'claudia_controlled_flows',
          component: ClaudiaControlledFlowsMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'controlled-flows/*',
          name: 'claudia_controlled_flows_catchall',
          component: ClaudiaControlledFlowsMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          // /tools/:workflowId/build (inline Eddie iframe inside the Tools
          // module). The catch-all keeps the sidebar's "Tools" entry active
          // because the route name still starts with `claudia_tools` (see
          // ClaudiaSecondarySidebar.vue → isActive uses .includes()).
          path: 'tools/*',
          name: 'claudia_tools_catchall',
          component: ClaudiaToolsView,
          meta: { permissions: ['administrator', 'cx_engineer'] },
        },
        {
          path: 'knowledge-bases',
          name: 'knowledge_base_view',
          component: KnowledgeBaseMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'knowledge-bases/*',
          name: 'knowledge_base_view_catchall',
          component: KnowledgeBaseMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'improvement-center',
          name: 'improvement_center_view',
          component: ImprovementCenterMicrofrontend,
          beforeEnter: guardImprovementCenterFlag,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
            newFeatureFlag: IMPROVEMENT_CENTER_FLAG,
          },
        },
        {
          path: 'improvement-center/*',
          name: 'improvement_center_view_catchall',
          component: ImprovementCenterMicrofrontend,
          beforeEnter: guardImprovementCenterFlag,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
            newFeatureFlag: IMPROVEMENT_CENTER_FLAG,
          },
        },
        {
          path: 'context-enrichment',
          name: 'claudia_context_enrichment',
          component: ClaudiaContextEnrichmentMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'context-enrichment/*',
          name: 'claudia_context_enrichment_catchall',
          component: ClaudiaContextEnrichmentMicrofrontend,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'agents',
          name: 'claudia_agents',
          component: ClaudiaAgentsView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'tools',
          name: 'claudia_tools',
          component: ClaudiaToolsView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'mcp',
          name: 'claudia_mcp',
          component: ClaudiaMcpView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'create-project',
          name: 'claudia_create_project',
          component: ClaudiaCreateProjectView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'ai-tagging-and-field-filling',
          name: 'claudia_ai_tagging_and_field_filling',
          component: ClaudiaAiTaggingAndFieldFillingView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'ai-tagging-and-field-filling/*',
          name: 'claudia_ai_tagging_and_field_filling_catchall',
          component: ClaudiaAiTaggingAndFieldFillingView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'settings',
          name: 'claudia_settings',
          component: ClaudiaSettingsView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
        {
          path: 'deploy',
          name: 'claudia_deploy',
          component: ClaudiaDeployView,
          meta: {
            permissions: ['administrator', 'cx_engineer'],
          },
        },
      ],
    },
  ],
};
